import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCoin } from '../CoinContext';

const SERVERS = ['Ain', 'Fang', 'Errai'];

const BOSSES = [
  'Lucid (Easy)', 'Lucid (Normal)', 'Lucid (Hard)',
  'Guardian Angel Slime',
  'Damien (Normal)', 'Damien (Hard)',
  'Lotus (Normal)', 'Lotus (Hard)',
  'Papulatus (Easy)', 'Papulatus (Normal)', 'Papulatus (Chaos)',
  'Cygnus (Easy)', 'Cygnus (Normal)',
  'Pink Bean (Normal)', 'Pink Bean (Chaos)',
  'Magnus (Easy)', 'Magnus (Normal)', 'Magnus (Hard)',
  'Arkarium (Easy)', 'Arkarium (Normal)',
  'OMNI-CLN',
  'Von Leon (Easy)', 'Von Leon (Normal)', 'Von Leon (Hard)',
  'Vellum (Normal)', 'Vellum (Chaos)',
  'Crimson Queen (Normal)', 'Crimson Queen (Chaos)',
  'Von Bon (Normal)', 'Von Bon (Chaos)',
  'Pierre (Normal)', 'Pierre (Chaos)',
  'Hilla (Normal)', 'Hilla (Hard)',
  'Horntail (Easy)', 'Horntail (Normal)', 'Horntail (Chaos)',
  'Zakum (Easy)', 'Zakum (Normal)', 'Zakum (Chaos)',
  'Balrog',
];

const CLASSES = [
  '딜러', '유틸', '시너지',
  'Hero', 'Paladin', 'Dark Knight',
  'Arch Mage F/P', 'Arch Mage I/L', 'Bishop',
  'Bowmaster', 'Marksman',
  'Night Lord', 'Shadower',
  'Buccaneer', 'Corsair',
  'Aran', 'Evan', 'Luminous', 'Mercedes', 'Phantom', 'Shade',
  'Dawn Warrior', 'Mihile', 'Blaze Wizard', 'Wind Archer', 'Night Walker', 'Thunder Breaker',
];

const LANGUAGES = [
  'English', 'Korean', 'Japanese', 'Chinese (Traditional)', 'Chinese (Simplified)',
  'Thai', 'Vietnamese', 'Indonesian', 'Malay', 'Filipino', 'Spanish', 'Portuguese',
];

const THEMES = {
  dark: {
    bg: '#121212',
    contentBg: 'rgba(18, 18, 18, 0.98)',
    cardBg: 'rgba(255,255,255,0.05)',
    inputBg: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.12)',
    text: '#e0e0e0',
    textSecondary: '#a0a0a0',
    textMuted: '#707070',
  },
  light: {
    bg: '#f5f5f5',
    contentBg: 'rgba(255, 255, 255, 0.98)',
    cardBg: 'rgba(0,0,0,0.03)',
    inputBg: 'rgba(0,0,0,0.05)',
    border: 'rgba(0,0,0,0.1)',
    text: '#1a1a1a',
    textSecondary: '#555',
    textMuted: '#888',
  },
};

const getPath = (path) => {
  const isCampSubdomain = window.location.hostname === 'camp.ssalsoongi.com';
  if (isCampSubdomain) {
    return path.replace(/^\/camp/, '') || '/';
  }
  return path;
};

export default function CampWritePage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditMode = !!editId;
  const { walletAddress, nickname } = useCoin();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [server, setServer] = useState('');
  const [boss, setBoss] = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [memberCount, setMemberCount] = useState(2);
  const [myCharacter, setMyCharacter] = useState(null);
  const [memberSlots, setMemberSlots] = useState([{ characterClass: '', cp: '', level: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);
  const [showCharacterModal, setShowCharacterModal] = useState(false);
  const [characters, setCharacters] = useState([]);
  const [characterLoading, setCharacterLoading] = useState(false);
  const [characterError, setCharacterError] = useState('');
  const [theme] = useState(() => {
    const savedMode = localStorage.getItem('campThemeMode');
    const savedTheme = localStorage.getItem('campTheme');
    if (savedMode === 'system' || !savedMode) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return savedTheme || 'dark';
  });

  const colors = THEMES[theme];

  // Load existing post data in edit mode
  useEffect(() => {
    if (!editId || !walletAddress) return;
    const loadPost = async () => {
      setLoadingPost(true);
      try {
        const { data, error } = await supabase
          .from('camp_posts')
          .select('*')
          .eq('id', editId)
          .single();
        if (error) throw error;
        if (data.author_wallet !== walletAddress) {
          navigate(getPath('/camp'), { replace: true });
          return;
        }
        setTitle(data.title || '');
        setContent(data.content || '');
        setServer(data.server || '');
        setBoss(data.boss || '');
        setSelectedLanguages(data.languages || []);
        if (data.member_count) setMemberCount(data.member_count);
        if (data.members && data.members.length > 0) {
          const owner = data.members.find(m => m.type === 'owner');
          if (owner && owner.name) {
            setMyCharacter({
              name: owner.name,
              level: owner.level,
              jobCode: owner.jobCode,
              jobName: owner.jobName,
              combatPower: owner.combatPower || null,
              assetKey: owner.assetKey,
              imageUrl: owner.imageUrl,
            });
          }
          const recruits = data.members.filter(m => m.type === 'recruit');
          if (recruits.length > 0) {
            setMemberSlots(recruits.map(r => ({
              characterClass: r.character_class || '',
              cp: r.cp ? String(r.cp) : '',
              level: r.level ? String(r.level) : '',
            })));
          }
        }
      } catch (err) {
        console.error('Failed to load post:', err);
        navigate(getPath('/camp'), { replace: true });
      } finally {
        setLoadingPost(false);
      }
    };
    loadPost();
  }, [editId, walletAddress]);

  if (!walletAddress) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: colors.bg,
        color: colors.textSecondary,
        gap: 20,
      }}>
        <p>Login required to write a post.</p>
        <button
          onClick={() => navigate(getPath('/camp'))}
          style={{
            background: colors.inputBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            padding: '10px 20px',
            cursor: 'pointer',
            color: colors.text,
            fontSize: 14,
          }}
        >
          Back to Camp
        </button>
      </div>
    );
  }

  const handleOpenCharacterModal = async () => {
    setCharacterError('');
    setCharacters([]);
    setShowCharacterModal(true);
    setCharacterLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setCharacterError('Login required.');
        setCharacterLoading(false);
        return;
      }
      const { data, error } = await supabase.functions.invoke('getUserCharacters', {
        body: { userId: walletAddress },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) {
        setCharacterError('Failed to load characters.');
      } else {
        const payload = data || {};
        const charactersFromApi = payload?.data?.characters || payload?.data?.data?.characters || [];
        const mapped = charactersFromApi.map((c) => ({
          id: c.assetKey || c.tokenId || c.name,
          assetKey: c.assetKey || null,
          name: c.name,
          level: c.data?.level,
          jobCode: c.data?.jobCode,
          jobName: c.data?.jobName || null,
          world: c.data?.world,
          imageUrl: c.data?.imageUrl,
        }));
        setCharacters(mapped);
      }
    } catch (err) {
      console.error(err);
      setCharacterError('Failed to load characters.');
    } finally {
      setCharacterLoading(false);
    }
  };

  const handleSelectCharacter = async (ch) => {
    setMyCharacter(ch);
    setShowCharacterModal(false);

    // assetKey가 있으면 getCharacterStat으로 jobName, level, CP 가져오기
    const assetKey = ch.assetKey;
    if (!assetKey) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : undefined;
      const { data, error } = await supabase.functions.invoke('getCharacterStat', {
        body: { assetKey },
        ...(headers && { headers }),
      });
      if (!error && data?.success && data?.data) {
        setMyCharacter(prev => ({
          ...prev,
          jobName: data.data.jobName || prev.jobName || null,
          level: data.data.level || prev.level || null,
          combatPower: data.data.combatPower || null,
        }));
      }
    } catch (err) {
      console.error('Failed to fetch character stat:', err);
    }
  };

  const handleMemberCountChange = (count) => {
    setMemberCount(count);
    const slotCount = count - 1;
    setMemberSlots(prev => {
      if (prev.length < slotCount) {
        return [...prev, ...Array(slotCount - prev.length).fill(null).map(() => ({ characterClass: '', cp: '', level: '' }))];
      }
      return prev.slice(0, slotCount);
    });
  };

  const updateSlot = (index, field, value) => {
    setMemberSlots(prev => prev.map((slot, i) => i === index ? { ...slot, [field]: value } : slot));
  };

  const toggleLanguage = (lang) => {
    setSelectedLanguages(prev =>
      prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]
    );
  };

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) return;
    setSubmitting(true);
    try {
      const authorName = nickname || `user_${walletAddress.slice(2, 8)}`;
      const postData = {
        title: title.trim(),
        content: content.trim(),
        server: server || null,
        boss: boss || null,
        languages: selectedLanguages.length > 0 ? selectedLanguages : null,
        member_count: memberCount,
        members: [
          myCharacter ? {
            type: 'owner',
            name: myCharacter.name,
            level: myCharacter.level || null,
            jobCode: myCharacter.jobCode || null,
            jobName: myCharacter.jobName || null,
            combatPower: myCharacter.combatPower || null,
            assetKey: myCharacter.assetKey || null,
            imageUrl: myCharacter.imageUrl || null,
          } : { type: 'owner' },
          ...memberSlots.map(s => ({
            type: 'recruit',
            character_class: s.characterClass || null,
            cp: s.cp ? Number(s.cp) : null,
            level: s.level ? Number(s.level) : null,
          })),
        ],
        author_nickname: authorName,
        author_wallet: walletAddress,
      };

      let error;
      if (isEditMode) {
        // Don't send author fields on update
        const { author_wallet, author_nickname, ...updateData } = postData;
        const { data, error: updateError } = await supabase
          .from('camp_posts')
          .update(updateData)
          .eq('id', editId)
          .eq('author_wallet', walletAddress)
          .select();
        error = updateError;
        if (!error && (!data || data.length === 0)) {
          throw new Error('Update failed. Please check RLS policies on camp_posts table.');
        }
      } else {
        ({ error } = await supabase.from('camp_posts').insert(postData));
      }
      if (error) throw error;
      if (isEditMode) {
        navigate(getPath(`/camp/post/${editId}`), { replace: true });
      } else {
        navigate(getPath('/camp'), { replace: true });
      }
    } catch (err) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} post:`, err);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} post. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: colors.bg,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 430,
        minHeight: '100vh',
        background: colors.contentBg,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 0 40px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}>
          <button
            onClick={() => navigate(getPath('/camp'))}
            style={{
              background: colors.inputBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: '10px 16px',
              cursor: 'pointer',
              color: colors.text,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            ← Back
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !title.trim() || !content.trim()}
            style={{
              background: (!title.trim() || !content.trim())
                ? colors.cardBg
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: 12,
              padding: '10px 20px',
              cursor: (!title.trim() || !content.trim()) ? 'not-allowed' : 'pointer',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? (isEditMode ? 'Saving...' : 'Posting...') : (isEditMode ? 'Save' : 'Post')}
          </button>
        </div>

        {/* Title */}
        <input
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{
            background: colors.inputBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            padding: '14px 16px',
            color: colors.text,
            fontSize: 16,
            fontWeight: 600,
            outline: 'none',
            marginBottom: 12,
          }}
        />

        {/* Server & Boss */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <select
            value={server}
            onChange={(e) => setServer(e.target.value)}
            style={{
              flex: 1,
              background: colors.inputBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 10,
              padding: '10px 12px',
              color: server ? colors.text : colors.textMuted,
              fontSize: 13,
              outline: 'none',
            }}
          >
            <option value="">Server</option>
            {SERVERS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={boss}
            onChange={(e) => setBoss(e.target.value)}
            style={{
              flex: 2,
              background: colors.inputBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 10,
              padding: '10px 12px',
              color: boss ? colors.text : colors.textMuted,
              fontSize: 13,
              outline: 'none',
            }}
          >
            <option value="">Boss</option>
            {BOSSES.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>

        {/* Content */}
        <textarea
          placeholder="Write your message..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          style={{
            background: colors.inputBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            padding: '14px 16px',
            color: colors.text,
            fontSize: 14,
            outline: 'none',
            minHeight: 200,
            resize: 'vertical',
            lineHeight: 1.6,
            marginBottom: 12,
          }}
        />

        {/* Languages */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 8 }}>Languages</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {LANGUAGES.map(lang => (
              <button
                key={lang}
                onClick={() => toggleLanguage(lang)}
                style={{
                  background: selectedLanguages.includes(lang)
                    ? 'rgba(102, 126, 234, 0.3)'
                    : colors.inputBg,
                  border: selectedLanguages.includes(lang)
                    ? '1px solid rgba(102, 126, 234, 0.5)'
                    : `1px solid ${colors.border}`,
                  borderRadius: 8,
                  padding: '6px 10px',
                  cursor: 'pointer',
                  color: selectedLanguages.includes(lang) ? colors.text : colors.textSecondary,
                  fontSize: 12,
                }}
              >
                {lang}
              </button>
            ))}
          </div>
        </div>

        {/* Members */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 8 }}>Members</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {[2, 3, 4, 5, 6].map(n => (
              <button
                key={n}
                onClick={() => handleMemberCountChange(n)}
                style={{
                  flex: 1,
                  background: memberCount === n ? 'rgba(102, 126, 234, 0.3)' : colors.inputBg,
                  border: memberCount === n ? '1px solid rgba(102, 126, 234, 0.5)' : `1px solid ${colors.border}`,
                  borderRadius: 8,
                  padding: '8px 0',
                  cursor: 'pointer',
                  color: memberCount === n ? colors.text : colors.textSecondary,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {n}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Slot 1: My Character */}
            <div
              onClick={handleOpenCharacterModal}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                background: colors.inputBg,
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: '8px 12px',
                cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}
            >
              <span style={{ fontSize: 12, color: colors.textMuted, minWidth: 16, textAlign: 'center' }}>1</span>
              {myCharacter ? (
                <>
                  {myCharacter.imageUrl && (
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, overflow: 'hidden',
                      backgroundColor: colors.cardBg, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <img src={myCharacter.imageUrl} alt="" style={{ width: '140%', height: '140%', objectFit: 'contain', transform: 'translateY(-4px)' }} />
                    </div>
                  )}
                  <span style={{ fontSize: 13, color: colors.text, fontWeight: 600, flex: 1 }}>{myCharacter.name}</span>
                  {(myCharacter.jobName || myCharacter.jobCode) && <span style={{ fontSize: 11, color: '#a78bfa' }}>{myCharacter.jobName || `Job ${myCharacter.jobCode}`}</span>}
                  {myCharacter.level && <span style={{ fontSize: 12, color: colors.textMuted }}>Lv.{myCharacter.level}</span>}
                  {myCharacter.combatPower && <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>CP {Number(myCharacter.combatPower).toLocaleString()}</span>}
                </>
              ) : (
                <span style={{ fontSize: 13, color: colors.textMuted, flex: 1 }}>Select My Character</span>
              )}
              <span style={{ fontSize: 12, color: colors.textMuted }}>▸</span>
            </div>
            {/* Slots 2+: Recruit */}
            {memberSlots.map((slot, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: colors.textMuted, minWidth: 16, textAlign: 'center' }}>{i + 2}</span>
                <select
                  value={slot.characterClass}
                  onChange={(e) => updateSlot(i, 'characterClass', e.target.value)}
                  style={{
                    flex: 2,
                    background: colors.inputBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 10,
                    padding: '10px 12px',
                    color: slot.characterClass ? colors.text : colors.textMuted,
                    fontSize: 13,
                    outline: 'none',
                  }}
                >
                  <option value="">Class</option>
                  {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input
                  type="number"
                  placeholder="CP"
                  value={slot.cp}
                  onChange={(e) => updateSlot(i, 'cp', e.target.value)}
                  style={{
                    flex: 1,
                    background: colors.inputBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 10,
                    padding: '10px 12px',
                    color: colors.text,
                    fontSize: 13,
                    outline: 'none',
                    minWidth: 0,
                  }}
                />
                <input
                  type="number"
                  placeholder="Lv"
                  value={slot.level}
                  onChange={(e) => updateSlot(i, 'level', e.target.value)}
                  style={{
                    flex: 1,
                    background: colors.inputBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 10,
                    padding: '10px 12px',
                    color: colors.text,
                    fontSize: 13,
                    outline: 'none',
                    minWidth: 0,
                  }}
                />
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Character Selection Modal */}
      {showCharacterModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => setShowCharacterModal(false)}
        >
          <div
            style={{
              backgroundColor: '#111827', borderRadius: 16,
              padding: 20, width: '100%', maxWidth: 640,
              maxHeight: '80vh', overflowY: 'auto',
              color: '#e5e7eb',
              boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Select My Character</h3>
              <button
                onClick={() => setShowCharacterModal(false)}
                style={{ background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 22 }}
              >×</button>
            </div>

            {characterLoading ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
            ) : characterError ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: '#fca5a5', fontSize: 13 }}>{characterError}</div>
            ) : characters.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No characters found.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 16 }}>
                {characters.map((ch) => (
                  <button
                    key={ch.id || ch.name}
                    onClick={() => handleSelectCharacter(ch)}
                    style={{
                      backgroundColor: '#020617', borderRadius: 18,
                      border: '1px solid #1f2937', padding: 14,
                      cursor: 'pointer', textAlign: 'center',
                      color: '#e5e7eb', fontSize: 12,
                      display: 'flex', flexDirection: 'column', gap: 10,
                      transition: 'transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 14px 30px rgba(0,0,0,0.55)';
                      e.currentTarget.style.borderColor = '#4b5563';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#1f2937';
                    }}
                  >
                    <div style={{
                      width: 96, height: 96, borderRadius: 20,
                      margin: '0 auto', overflow: 'hidden',
                      background: 'radial-gradient(circle at 30% 0%, #1f2937, #020617 60%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {ch.imageUrl ? (
                        <img src={ch.imageUrl} alt={ch.name || ''} style={{
                          width: '150%', height: '150%', objectFit: 'contain',
                          objectPosition: 'center center', transform: 'translateY(-8px)',
                        }} />
                      ) : (
                        <span style={{ fontSize: 13, color: '#6b7280' }}>{ch.name || 'Character'}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{ch.name}</div>
                    {(ch.jobName || ch.jobCode) && <div style={{ fontSize: 10, color: '#a78bfa' }}>{ch.jobName || `Job ${ch.jobCode}`}</div>}
                    {ch.level && <div style={{ fontSize: 11, color: '#9ca3af' }}>Lv.{ch.level}</div>}
                  </button>
                ))}
              </div>
            )}

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCharacterModal(false)}
                style={{ padding: '8px 14px', borderRadius: 8, backgroundColor: '#374151', color: '#e5e7eb', border: 'none', fontSize: 13, cursor: 'pointer' }}
              >Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
